// ──────────────────────────────────────────────────────────────────────────────
// Helper de fechas — interpreta strings de la BD como UTC y los muestra en
// Europe/Madrid usando Intl.DateTimeFormat (que aplica DST automáticamente).
//
// La BD devuelve fechas en formato "YYYY-MM-DD HH:MM:SS" sin información de
// zona. Las interpretamos como UTC (que es como las guarda MySQL por defecto
// en Hostinger). El navegador del usuario convierte a hora de Madrid al
// renderizar, sin librerías externas.
// ──────────────────────────────────────────────────────────────────────────────

const TZ = 'Europe/Madrid';

// Convierte cualquier valor de fecha (string SQL, ISO con Z, o Date) a un Date.
// Si es un string SQL "YYYY-MM-DD HH:MM:SS" (sin zona) lo trata como UTC.
export function parseUTC(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) return isNaN(value) ? null : value;
  let s = String(value).trim();
  if (!s) return null;
  // Sustituir el espacio por T para que JS lo parsee como ISO
  s = s.replace(' ', 'T');
  // Si no tiene zona horaria (Z o ±HH:MM) lo asumimos UTC
  if (!/Z$|[+-]\d{2}:?\d{2}$/.test(s)) s += 'Z';
  const d = new Date(s);
  return isNaN(d) ? null : d;
}

// Genera un formateador cacheado por opciones (la creación de Intl es lenta).
const _fmtCache = new Map();
function getFmt(opts) {
  const key = JSON.stringify(opts);
  let f = _fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat('es-ES', { timeZone: TZ, ...opts });
    _fmtCache.set(key, f);
  }
  return f;
}

// dd/mm/aa (corto, sin hora)
export function fmtDate(value) {
  const d = parseUTC(value);
  return d ? getFmt({ day: '2-digit', month: '2-digit', year: '2-digit' }).format(d) : '—';
}

// dd/mm/aaaa (largo, sin hora)
export function fmtDateLong(value) {
  const d = parseUTC(value);
  return d ? getFmt({ day: '2-digit', month: '2-digit', year: 'numeric' }).format(d) : '—';
}

// dd/mm/aa HH:MM (con hora, sin segundos)
export function fmtMin(value) {
  const d = parseUTC(value);
  if (!d) return '—';
  return getFmt({
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(d);
}

// dd/mm/aa HH:MM:SS (con segundos)
export function fmtSec(value) {
  const d = parseUTC(value);
  if (!d) return '—';
  return getFmt({
    day: '2-digit', month: '2-digit', year: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).format(d);
}

// "4 de junio de 2026" (largo legible)
export function fmtDateText(value) {
  const d = parseUTC(value);
  return d ? getFmt({ day: '2-digit', month: 'long', year: 'numeric' }).format(d) : '—';
}

// HH:MM (sólo hora)
export function fmtTime(value) {
  const d = parseUTC(value);
  return d ? getFmt({ hour: '2-digit', minute: '2-digit', hour12: false }).format(d) : '—';
}

// Devuelve string "YYYY-MM-DD" en hora Madrid — útil para comparar/filtrar
// fechas por día (ej. agrupar por día en calendario).
export function dayKey(value) {
  const d = parseUTC(value);
  if (!d) return null;
  const parts = getFmt({ year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(d);
  const y = parts.find(p => p.type === 'year').value;
  const m = parts.find(p => p.type === 'month').value;
  const dd = parts.find(p => p.type === 'day').value;
  return `${y}-${m}-${dd}`;
}

// Devuelve número de ms del epoch — para comparar/ordenar dos fechas.
export function toMs(value) {
  const d = parseUTC(value);
  return d ? d.getTime() : 0;
}
